const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${BACKEND_URL}/api`;

// Called when a silent token refresh fails mid-session so the UI can drop back to a
// signed-out state without every screen having to know about token plumbing.
let onUnauthorized = null;
export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

function authHeader() {
  const token = localStorage.getItem("yp_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function setTokens(access, refresh) {
  localStorage.setItem("yp_token", access);
  localStorage.setItem("yp_refresh", refresh);
}

function clearTokens() {
  localStorage.removeItem("yp_token");
  localStorage.removeItem("yp_refresh");
}

// De-duplicates concurrent refresh attempts: if several requests 401 around the same time,
// they all await the same in-flight refresh instead of racing the server's one-time-use
// refresh-token rotation against each other.
let refreshing = null;

async function tryRefresh() {
  if (!refreshing) {
    refreshing = (async () => {
      const refresh_token = localStorage.getItem("yp_refresh");
      if (!refresh_token) return false;
      try {
        const res = await fetch(`${BASE}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token }),
        });
        if (!res.ok) return false;
        const j = await res.json();
        setTokens(j.access_token, j.refresh_token);
        return true;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

async function request(path, options = {}, auth = false, _retried = false) {
  const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
  if (auth) Object.assign(headers, authHeader());
  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  if (res.status === 401 && auth && !_retried) {
    const refreshed = await tryRefresh();
    if (refreshed) return request(path, options, auth, true);
    clearTokens();
    onUnauthorized?.();
  }

  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const j = await res.json();
      detail = j.detail || detail;
    } catch {}
    throw new Error(formatApiErrorDetail(detail));
  }
  if (res.status === 204) return null;
  return res.json();
}

function saveSession(res) {
  setTokens(res.access_token, res.refresh_token);
  return res.user;
}

export const api = {
  home: () => request("/home"),
  categories: () => request("/categories"),
  filters: { global: () => request("/filters/global") },
  careLevels: () => request("/care-levels"),
  products: async (params = {}) => {
    const p = { ...params };
    if (p.attributes && typeof p.attributes === "object") {
      p.attributes = Object.keys(p.attributes).length ? JSON.stringify(p.attributes) : undefined;
    }
    const qs = new URLSearchParams(
      Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== ""),
    ).toString();
    const res = await fetch(`${BASE}/products${qs ? `?${qs}` : ""}`);
    const items = await res.json();
    return { items, total: Number(res.headers.get("X-Total-Count") || items.length) };
  },
  product: (id) => request(`/products/${id}`),
  search: {
    suggest: (q) => request(`/search/suggest?q=${encodeURIComponent(q)}`),
  },
  pincode: (pin) => request(`/pincode/${pin}`),
  reverseGeocode: (lat, lng) => request(`/geocode/reverse?lat=${lat}&lng=${lng}`),
  blogs: async (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
    ).toString();
    const res = await fetch(`${BASE}/blogs${qs ? `?${qs}` : ""}`);
    const items = await res.json();
    return { items, total: Number(res.headers.get("X-Total-Count") || items.length) };
  },
  blog: (url) => request(`/blogs/${url}`),
  services: () => request("/services"),
  service: (id) => request(`/services/${id}`),
  reviews: (productId) => request(`/products/${productId}/reviews`),
  addReview: (productId, body) =>
    request(`/products/${productId}/reviews`, { method: "POST", body: JSON.stringify(body) }, true),

  register: (body) => request("/auth/register", { method: "POST", body: JSON.stringify(body) }).then(saveSession),
  login: (body) => request("/auth/login", { method: "POST", body: JSON.stringify(body) }).then(saveSession),
  logout: async () => {
    const refresh_token = localStorage.getItem("yp_refresh");
    try {
      await request("/auth/logout", { method: "POST", body: JSON.stringify({ refresh_token }) });
    } catch {
      // best-effort — still clear the local session either way
    }
    clearTokens();
  },
  me: () => request("/auth/me", {}, true),
  verifyEmail: (email, code) => request("/auth/verify-email", { method: "POST", body: JSON.stringify({ email, code }) }),
  resendVerification: (email) => request("/auth/resend-verification", { method: "POST", body: JSON.stringify({ email }) }),

  cart: {
    get: () => request("/cart", {}, true),
    set: (items) => request("/cart", { method: "PUT", body: JSON.stringify({ items }) }, true),
  },
  wishlist: {
    get: () => request("/wishlist", {}, true),
    set: (product_ids) => request("/wishlist", { method: "PUT", body: JSON.stringify({ product_ids }) }, true),
  },

  createOrder: (body) => request("/orders", { method: "POST", body: JSON.stringify(body) }, true),
  order: (id) => request(`/orders/${id}`),
  verifyPayment: (orderId, body) =>
    request(`/orders/${orderId}/verify-payment`, { method: "POST", body: JSON.stringify(body) }),
  myOrders: () => request("/orders", {}, true),

  serviceRequests: {
    create: (body) => request("/service-requests", { method: "POST", body: JSON.stringify(body) }, true),
    mine: () => request("/service-requests", {}, true),
    verifyPayment: (id, body) =>
      request(`/service-requests/${id}/verify-payment`, { method: "POST", body: JSON.stringify(body) }),
  },
  upload: async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const res = await fetch(`${BASE}/uploads`, { method: "POST", body: fd });
    if (!res.ok) {
      let detail = `Upload failed (${res.status})`;
      try {
        const j = await res.json();
        detail = j.detail || detail;
      } catch {}
      throw new Error(formatApiErrorDetail(detail));
    }
    return res.json();
  },
  lead: (body) => request("/leads", { method: "POST", body: JSON.stringify(body) }),
};

export function inr(v) {
  const n = Number(v || 0);
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
